import { useState, useEffect } from 'react';
import { parksApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const regions = ['Essex', 'Suffolk', 'Kent', 'Norfolk', 'Cambridgeshire', 'Hertfordshire', 'London', 'Other'];

export default function ParksPage() {
  const [parks, setParks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPark, setEditingPark] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    region: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await parksApi.getAll();
      setParks(data);
    } catch (error) {
      toast.error('Failed to load parks');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', region: '', contact_name: '', contact_phone: '', contact_email: '', notes: '' });
    setEditingPark(null);
  };

  const openDialog = (park = null) => {
    if (park) {
      setEditingPark(park);
      setFormData({
        name: park.name || '',
        address: park.address || '',
        region: park.region || '',
        contact_name: park.contact_name || '',
        contact_phone: park.contact_phone || '',
        contact_email: park.contact_email || '',
        notes: park.notes || '',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPark) {
        await parksApi.update(editingPark.id, formData);
        toast.success('Park updated');
      } else {
        await parksApi.create(formData);
        toast.success('Park created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save park');
    }
  };

  const handleDelete = async (park) => {
    if (!window.confirm('Delete this park?')) return;
    try {
      await parksApi.delete(park.id);
      toast.success('Park deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete park');
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'address', label: 'Address' },
    { key: 'region', label: 'Region', render: (val) => val || '-' },
    { key: 'contact_name', label: 'Contact', render: (val) => val || '-' },
    { key: 'contact_phone', label: 'Phone', render: (val) => val || '-' },
    { key: 'created_at', label: 'Added', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const actions = [
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="parks-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Parks & Sites</h1>
          <p className="text-sm text-stone-500 mt-1">Manage holiday parks and sites</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-park-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Park
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={parks}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No parks found. Add your first park!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="park-dialog">
          <DialogHeader>
            <DialogTitle>{editingPark ? 'Edit Park' : 'Add New Park'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Park Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="park-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                data-testid="park-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                placeholder="e.g. Essex, Suffolk, Kent"
                data-testid="park-region-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  data-testid="park-contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  data-testid="park-contact-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  data-testid="park-contact-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="park-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="park-submit-btn">
                {editingPark ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
